/**
 * WordPress dependencies
 */
import { SelectControl } from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';
import type { WPUnitControlUnit } from '@wordpress/components/build-types/unit-control/types';

/**
 * External dependencies
 */
import clsx from 'clsx';
import { BoxControl } from './wordpress/components/box-control';
import type { BoxControlValue } from './wordpress/components/box-control/types';

/**
 * Control to display position options.
 *
 * @param {Object} props Component props.
 *
 * @return {Element} Position control.
 */
type PositionControlProps = {
	readonly __next40pxDefaultSize?: boolean;
	readonly values: {
		readonly position?: string;
		readonly top?: string;
		readonly right?: string;
		readonly bottom?: string;
		readonly left?: string;
	};
	readonly onChange: ( value: {
		readonly position?: string;
		readonly top?: string;
		readonly right?: string;
		readonly bottom?: string;
		readonly left?: string;
	} ) => void;
	readonly units?: WPUnitControlUnit[];
	readonly sides?: ( keyof BoxControlValue )[];
	readonly size?: string;
	readonly disabledPosition?: boolean;
	readonly disabledSides: Record<
		'top' | 'right' | 'bottom' | 'left',
		boolean
	>;
};
export function PositionControl(
	props: PositionControlProps
): JSX.Element | false {
	const {
		/** Start opting into the larger default height that will become the default size in a future version. */
		__next40pxDefaultSize = false,
		onChange,
		values,
		units,
		sides,
		disabledPosition,
		disabledSides,
	} = props;

	const selectOptions = [
		{ value: 'static', label: 'Static' },
		{ value: 'relative', label: 'Relative' },
		{ value: 'absolute', label: 'Absolute' },
		{ value: 'fixed', label: 'Fixed' },
		{ value: 'sticky', label: 'Sticky' },
	];

	// Find current selection by comparing position against options,
	// and fall back to the Default option if there is no matching option.
	const currentSelection =
		selectOptions.find( ( option ) => option.value === values.position ) ||
		selectOptions[ 0 ];

	return (
		<div
			className={ clsx( 'nab-components-position-control', {
				'nab-components--disabled': disabledPosition,
			} ) }
		>
			<SelectControl
				disabled={ disabledPosition }
				className={ clsx( 'components-position-control', {
					'nab-components--disabled': disabledPosition,
				} ) }
				__next40pxDefaultSize={ __next40pxDefaultSize }
				label={ _x( 'Position', 'text', 'nelio-ab-testing' ) }
				options={ selectOptions }
				value={ currentSelection?.value }
				onChange={ ( selectedValue ) =>
					onChange( { position: selectedValue } )
				}
			/>
			<BoxControl
				__next40pxDefaultSize
				label={ _x(
					'Position Coordinates',
					'text',
					'nelio-ab-testing'
				) }
				values={ values }
				onChange={ onChange }
				sides={ sides }
				units={ units }
				disabledSides={ disabledSides }
				showAllSides
				allowReset={ false }
			/>
		</div>
	);
}
